import { useState } from 'react';
import { List, useListContext, useRedirect, useNotify, useRefresh } from 'react-admin';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, ToggleButtonGroup, ToggleButton, Alert,
} from '@mui/material';
import {
  tbl, btn, inputSx, badgeStyle, NAVY,
  WEBSITE_ISSUE_STATUS, WEBSITE_MAPPING_ISSUE_TYPE_LABELS,
} from '../styles/ds';
import EmptyState from '../components/EmptyState';
import { websiteRequest } from './websiteApi';

const fmtDate = iso => (iso ? new Date(iso).toLocaleString('he-IL') : '—');

/** Human summary of an issue's recorded CRM data (variant ids are shown, never changed). */
export const IssueDetails = ({ issue }) => {
  const d = issue.details || {};
  if (issue.issue_type === 'multiple_sizes_for_simple_script') {
    return (
      <Box>
        <Typography fontSize={13}>מידה קבועה של מוצר האתר: <b dir="ltr">{d.listing_size || '—'}</b></Typography>
        {Object.entries(d.crm_sizes || {}).map(([size, ids]) => (
          <Typography key={size} fontSize={12} color="text.secondary">
            מידה <b dir="ltr">{size}</b> ב-CRM: וריאנטים {ids.map(id => `#${id}`).join(', ')}
          </Typography>
        ))}
      </Box>
    );
  }
  if (issue.issue_type === 'unverifiable_variant_size') {
    return (
      <Typography fontSize={13}>
        לווריאנטים {(d.crm_variant_ids || []).map(id => `#${id}`).join(', ')} אין מידה ב-CRM — לא ניתן לוודא שהם במידה <b dir="ltr">{d.listing_size || '—'}</b>
      </Typography>
    );
  }
  return <Typography fontSize={12} color="text.secondary" dir="ltr">{JSON.stringify(d)}</Typography>;
};

/**
 * Closes one open issue. The server refuses while the cause is still present
 * (WD-035: nothing is resolved automatically), so the dialog says what to fix.
 */
export const ResolveIssueDialog = ({ issue, onClose, onResolved }) => {
  const notify = useNotify();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await websiteRequest(`/website_mapping_issues/${issue.id}/resolve`, 'POST', { note: note.trim() || null });
      notify('הבעיה נסגרה', { type: 'success' });
      onResolved();
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} dir="rtl" maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>סגירת בעיית מיפוי</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography fontWeight={600}>{WEBSITE_MAPPING_ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}</Typography>
          <IssueDetails issue={issue} />
          <Alert severity="info">
            ניתן לסגור רק אחרי תיקון המיפוי: למפות את כל הצירופים לווריאנטים במידה הקבועה (או לשנות את המידה הקבועה) ולשמור.
            המערכת אינה בוחרת וריאנט או מידה באופן אוטומטי.
          </Alert>
          <TextField label="הערת החלטה (נרשמת ביומן)" value={note} onChange={e => setNote(e.target.value)}
            multiline minRows={2} fullWidth sx={inputSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={btn.cancel}>ביטול</Button>
        <Button variant="contained" onClick={submit} disabled={busy} sx={btn.primary}>סגירת הבעיה</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Issues table — used on the issues page and inside a listing's edit page. */
export const IssuesTable = ({ issues, showProduct = false, onChanged, readOnly = false }) => {
  const redirect = useRedirect();
  const [resolving, setResolving] = useState(null);
  return (
    <>
      <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
        <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
          <TableHead><TableRow>
            {showProduct && <TableCell>מוצר אתר</TableCell>}
            <TableCell>סוג</TableCell>
            <TableCell>פרטים</TableCell>
            <TableCell align="center">מצב</TableCell>
            <TableCell>נפתחה</TableCell>
            <TableCell>החלטה</TableCell>
            <TableCell align="center">פעולה</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {issues.map((i, idx) => (
              <TableRow key={i.id} sx={tbl.zebraRow(idx)}>
                {showProduct && (
                  <TableCell>
                    <Button size="small" onClick={() => redirect('edit', 'website_products', i.product_id)} sx={{ textTransform: 'none' }}>
                      <span dir="ltr">{i.product_name}</span>
                    </Button>
                  </TableCell>
                )}
                <TableCell><Typography fontSize={13} fontWeight={600}>{WEBSITE_MAPPING_ISSUE_TYPE_LABELS[i.issue_type] || i.issue_type}</Typography></TableCell>
                <TableCell><IssueDetails issue={i} /></TableCell>
                <TableCell align="center"><span style={badgeStyle(WEBSITE_ISSUE_STATUS, i.status)}>{WEBSITE_ISSUE_STATUS[i.status]?.label}</span></TableCell>
                <TableCell><Typography fontSize={12} color="text.secondary">{fmtDate(i.created_at)}</Typography></TableCell>
                <TableCell>
                  <Typography fontSize={12} color="text.secondary">
                    {i.status === 'resolved' ? `${fmtDate(i.resolved_at)}${i.resolution_note ? ` · ${i.resolution_note}` : ''}` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {i.status === 'open' && !readOnly && (
                    <Button size="small" variant="outlined" sx={btn.gold} onClick={() => setResolving(i)}>סגירה</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></Box>
      </TableContainer>
      {resolving && (
        <ResolveIssueDialog issue={resolving} onClose={() => setResolving(null)}
          onResolved={() => { setResolving(null); onChanged(); }} />
      )}
    </>
  );
};

const IssuesPageBody = () => {
  const { data = [], isLoading, filterValues, setFilters } = useListContext();
  const refresh = useRefresh();
  const status = filterValues?.status || 'all';
  return (
    <Box>
      <Box px={1} py={1.5}>
        <ToggleButtonGroup size="small" exclusive value={status} aria-label="סינון לפי מצב"
          onChange={(e, v) => v && setFilters(v === 'all' ? {} : { status: v }, null, false)}>
          <ToggleButton value="open">פתוחות</ToggleButton>
          <ToggleButton value="resolved">נסגרו</ToggleButton>
          <ToggleButton value="all">הכול</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {isLoading ? null : data.length === 0
        ? <EmptyState title={status === 'open' ? 'אין בעיות מיפוי פתוחות' : 'אין בעיות מיפוי'} />
        : <IssuesTable issues={data} showProduct onChanged={refresh} />}
    </Box>
  );
};

export const WebsiteMappingIssueList = () => (
  <List className="crm-mobile-list crm-inline-list" filterDefaultValues={{ status: 'open' }} perPage={1000}
    pagination={false} storeKey={false} actions={false} title="בעיות מיפוי — אתר" sx={tbl.listSx}>
    <IssuesPageBody />
  </List>
);
